/**
 * EffectiveConfig - Wave 5 Group-C (2026-05-27).
 *
 * Surfaces the unified resolver output for the current tenant or a
 * specific Business Unit: which packs are active, which classifier
 * overlays apply, which expected systems / ROPA templates / toxic
 * combinations / DDR playbooks / regulatory clauses ride along.
 *
 * Backend has resolved this for weeks (regulatory replay needs it)
 * but no UI exposed it - DPOs couldn't answer "what's actually
 * being enforced for my Treasury BU right now?".
 */
import { Rx } from '@/lib/regulation-terms';
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  Layers,
  Filter,
  Hash,
  Database,
  ClipboardCheck,
  AlertTriangle,
  Workflow,
  ScrollText,
  RefreshCw,
  Building2,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import { LoadingState, ErrorState } from '@foctta/ui-shell';
import { EmptyStateV2 } from '@/components/ui/empty-state-v2';
import { useMotionPreset } from '@/lib/motion-level';
import { PackOriginChips } from '@/components/industry/PackProvenance';
import {
  getEffectiveConfig,
  listBusinessUnits,
  type EffectiveConfig,
  type BusinessUnit,
} from '@/lib/api';

const SEVERITY_VARIANT: Record<string, 'critical' | 'warning' | 'glass'> = {
  critical: 'critical',
  high: 'warning',
  medium: 'glass',
};

export function EffectiveConfigPage(): JSX.Element {
  const [config, setConfig] = useState<EffectiveConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bus, setBus] = useState<BusinessUnit[]>([]);
  const [selectedBuId, setSelectedBuId] = useState<string | undefined>(undefined);
  const { transition, rise } = useMotionPreset();

  useEffect(() => {
    listBusinessUnits({ status: 'active' })
      .then((res) => setBus(res.businessUnits))
      .catch(() => {
        /* fine - BU dropdown optional */
      });
  }, []);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const result = await getEffectiveConfig(selectedBuId);
      setConfig(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuId]);

  return (
    <PageContainer>
      <PageHeader
        title="Effective Configuration"
        subtitle="The union of every classifier overlay, expected system, ROPA template, toxic combination, DDR playbook, and regulatory clause that applies under the current taxonomy snapshot."
        badges={[
          { label: 'Taxonomy resolver', variant: 'glass' },
          ...(config
            ? [
                { label: config.resolution, variant: 'info' as const },
                {
                  label: selectedBuId ? 'Per-BU scope' : 'Tenant default',
                  variant: 'success' as const,
                },
              ]
            : []),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        }
      />

      <GlassCard className="p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Filter className="h-3.5 w-3.5" /> Scope:
          </div>
          <Button
            size="sm"
            variant={selectedBuId === undefined ? 'default' : 'glass'}
            onClick={() => setSelectedBuId(undefined)}
          >
            Tenant default
          </Button>
          {bus.map((bu) => (
            <Button
              key={bu.id}
              size="sm"
              variant={selectedBuId === bu.id ? 'default' : 'glass'}
              onClick={() => setSelectedBuId(bu.id)}
            >
              <Building2 className="h-3 w-3 mr-1.5" />
              {bu.name}
            </Button>
          ))}
        </div>
      </GlassCard>

      {loading && !config && <LoadingState caption="Resolving effective configuration…" />}
      {error && (
        <ErrorState
          title="Failed to resolve effective config"
          detail={error}
          onRetry={() => void refresh()}
        />
      )}

      {config && (
        <>
          {/* Wave 8 (2026-05-27) - KPI strip rises with the page so
              the eye lands on the numbers first. */}
          <motion.div
            initial={rise.hidden}
            animate={rise.visible}
            transition={transition}
            className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6"
          >
            <KpiCard label="Active packs" value={config.activePackIds.length} icon={Layers} />
            <KpiCard label="Overlays" value={config.classifierOverlays.length} icon={Hash} />
            <KpiCard label="Expected systems" value={config.expectedSystems.length} icon={Database} />
            <KpiCard label="ROPA templates" value={config.ropaTemplates.length} icon={ClipboardCheck} />
            <KpiCard
              label="Toxic combos"
              value={config.toxicCombinations.length}
              icon={AlertTriangle}
              glow={config.toxicCombinations.length > 0 ? 'medium' : 'low'}
            />
            <KpiCard label="DDR playbooks" value={config.ddrPlaybooks.length} icon={Workflow} />
          </motion.div>

          {/* Active packs strip */}
          <GlassCard className="p-4 mb-4">
            <CardHeader className="p-0 mb-2">
              <CardTitle className="text-sm">Active packs</CardTitle>
              <CardDescription>
                Resolved via <code className="font-mono text-[10px]">{config.resolution}</code>.
              </CardDescription>
            </CardHeader>
            <div className="flex flex-wrap gap-1.5">
              {config.activePackIds.map((id) => (
                <Badge key={id} variant="info" className="text-[10px] font-mono">
                  {id}
                </Badge>
              ))}
            </div>
            {(config.provenance ?? []).length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                  Discovery packs in force, and why
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <PackOriginChips packs={config.provenance ?? []} />
                </div>
              </div>
            )}
          </GlassCard>

          {/* Classifier overlays */}
          {config.classifierOverlays.length > 0 && (
            <motion.div initial={rise.hidden} animate={rise.visible} transition={transition}>
              <GlassCard className="p-5 mb-4">
                <CardHeader className="p-0 mb-3">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Hash className="h-4 w-4 text-primary" /> Classifier overlays
                  </CardTitle>
                  <CardDescription>
                    <Rx>PII labels boosted by these packs.</Rx> Each overlay carries regex + keyword sets
                    and a confidence boost.
                  </CardDescription>
                </CardHeader>
                <div className="space-y-2">
                  {config.classifierOverlays.map((o, i) => (
                    <div
                      key={`${o.packId}-${o.label}-${i}`}
                      className="glass-lite rounded-md p-3 text-xs"
                    >
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <code className="font-mono">{o.label}</code>
                        <Badge variant="glass" className="text-[9px]">
                          {o.packId}
                        </Badge>
                        <Badge variant="info" className="text-[9px]">
                          +{(o.confidenceBoost * 100).toFixed(0)}% confidence
                        </Badge>
                        {o.regulations.map((r) => (
                          <Badge key={r} variant="glass" className="text-[9px] uppercase">
                            {r}
                          </Badge>
                        ))}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {o.regexPatterns.length} regex · {o.keywords.length} keywords
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </motion.div>
          )}

          {/* Toxic combinations */}
          {config.toxicCombinations.length > 0 && (
            <GlassCard className="p-5 mb-4">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-warning" /> Toxic combinations
                </CardTitle>
                <CardDescription>
                  <Rx>PII label combinations that trigger automatic risk escalation.</Rx>
                </CardDescription>
              </CardHeader>
              <div className="space-y-2">
                {config.toxicCombinations.map((c, i) => (
                  <div key={i} className="glass-lite rounded-md p-3 text-xs">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant={SEVERITY_VARIANT[c.severity]} className="text-[9px] uppercase">
                        {c.severity}
                      </Badge>
                      {c.labels.map((l) => (
                        <code key={l} className="font-mono">
                          {l}
                        </code>
                      ))}
                      <Badge variant="glass" className="text-[9px]">
                        {c.packId}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{c.rationale}</p>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          {/* DDR playbooks */}
          {config.ddrPlaybooks.length > 0 && (
            <GlassCard className="p-5 mb-4">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Workflow className="h-4 w-4 text-primary" /> DDR playbooks in scope
                </CardTitle>
              </CardHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {config.ddrPlaybooks.map((p) => (
                  <div key={p.playbookId} className="glass-lite rounded-md p-2 text-xs">
                    <code className="font-mono">{p.name}</code>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {p.regulations.map((r) => (
                        <Badge key={r} variant="glass" className="text-[9px] uppercase">
                          {r}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Regulatory clauses */}
          {config.regulatoryClauses.length > 0 && (
            <GlassCard className="p-5">
              <CardHeader className="p-0 mb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <ScrollText className="h-4 w-4 text-primary" /> Regulatory clauses (union)
                </CardTitle>
                <CardDescription clamp>
                  Every regulation clause this configuration claims coverage for, across all
                  active packs.
                </CardDescription>
              </CardHeader>
              <div className="flex flex-wrap gap-1.5">
                {config.regulatoryClauses.map((c) => (
                  <Badge key={c} variant="glass" className="text-[10px] font-mono">
                    {c}
                  </Badge>
                ))}
              </div>
            </GlassCard>
          )}
        </>
      )}

      {!loading && !config && !error && (
        <EmptyStateV2
          icon={Layers}
          title="No effective configuration"
          description="No active packs or overlays found for this scope. Activate an industry pack on /admin/industry-packs to get started."
          secondaryAction={{ label: 'Activate industry packs', href: '/admin/industry-packs' }}
        />
      )}
    </PageContainer>
  );
}
